import {
  Leaf,
  Recycle,
  Package,
  Handshake,
  Check,
  Factory,
  Award,
  Shield,
  Sprout,
  Box,
} from 'lucide-react';
import { Button } from './ui/button';

const baseUrl = process.env.PUBLIC_URL || '';
const assetsUrl = `${baseUrl}/assets/decor`;

const PILLARS = [
  {
    title: 'Responsible Materials',
    icon: Leaf,
    points: ['Eco-friendly jute, cotton & canvas', 'Reusable & durable choices', 'Reduced plastic dependency'],
  },
  {
    title: 'Ethical Sourcing',
    icon: Handshake,
    points: ['Traceable supply chains', 'Fair labor practices', 'Local sourcing where possible'],
  },
  {
    title: 'Responsible Manufacturing',
    icon: Factory,
    points: ['Efficient resource use', 'Waste reduction', 'Clean production processes'],
  },
  {
    title: 'Long-Term Partnerships',
    icon: Sprout,
    points: ['Stable supplier relationships', 'Continuous improvement', 'Shared sustainability goals'],
  },
];

const MATERIALS = [
  {
    title: 'Jute & Natural Fibers',
    image: `${baseUrl}/45.png`,
    points: ['Renewable, biodegradable', 'Strong and durable'],
  },
  {
    title: 'Cotton & Canvas',
    image: `${baseUrl}/42.png`,
    points: ['Reusable and washable', 'Low environmental footprint'],
  },
  {
    title: 'Custom & Printed',
    image: `${baseUrl}/34.png`,
    points: ['Eco-friendly inks', 'Recyclable components'],
  },
  {
    title: 'Promotional Bags',
    image: `${baseUrl}/47.png`,
    points: ['Bulk reusable options', 'Brand-ready sustainable choice'],
  },
];

const IMPACT_STATS = [
  { icon: Recycle, value: '100%', label: 'Reusable Products' },
  { icon: Package, value: 'Plastic-Free', label: 'Manufacturing Focus' },
  { icon: Box, value: 'Recyclable', label: 'Packaging Options' },
  { icon: Sprout, value: 'Sustainable', label: 'Farming Support' },
];

const CERTIFICATIONS = [
  { name: 'ISO Compliant', icon: Award },
  { name: 'REACH Compliant', icon: Shield },
  { name: 'AZO-Free Dyes', icon: Leaf },
  { name: 'Export Quality Standards', icon: Award },
];

export const SustainabilityPage = ({ onRequestQuote }) => {
  return (
    <div className="bg-[#F5F2ED] min-h-screen">
      {/* Section 1 — Hero */}
      <section
        className="relative pt-24 md:pt-28 pb-20 lg:pb-28 bg-[#F5F2ED] overflow-hidden"
        style={{ backgroundImage: `url('${assetsUrl}/weave-pattern.svg')` }}
      >
        <div className="absolute bottom-0 left-0 w-32 h-32 lg:w-40 lg:h-40 opacity-[0.15] pointer-events-none">
          <img src={`${assetsUrl}/leaf-corner.svg`} alt="" className="w-full h-full object-contain object-left-bottom" aria-hidden />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1F2937] leading-tight mb-6">
                Sustainability at the Core of Every Bag
              </h1>
              <p className="text-[#5A5A5A] text-base leading-relaxed mb-8 max-w-lg">
                We believe in bags that last—made from responsible materials, sourced ethically, and
                manufactured with care for people and the planet.
              </p>
              <Button
                onClick={onRequestQuote}
                className="bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md px-6 py-5 text-sm font-medium transition-all duration-200 shadow-[0_2px_12px_rgba(47,79,62,0.2)] hover:shadow-[0_4px_20px_rgba(47,79,62,0.25)]"
              >
                Request Wholesale Quote
              </Button>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(31,41,55,0.08)] bg-[#E8E2DA] relative">
                <img
                  src={`${baseUrl}/Sus_pro.png`}
                  alt="Sustainable bag collection"
                  className="w-full h-auto min-h-[280px] object-cover"
                />
                <img
                  src={`${assetsUrl}/plant-decor.png`}
                  alt=""
                  className="absolute bottom-2 right-2 w-16 h-16 lg:w-20 lg:h-20 object-contain opacity-90 pointer-events-none"
                  aria-hidden
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 — Our Sustainability Pillars */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#1F2937] text-center mb-14 lg:mb-16">
            Our Sustainability Pillars
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {PILLARS.map((item, i) => (
              <div
                key={i}
                className="bg-[#F5F2ED] border border-[#E8E2DA] rounded-xl p-7 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] transition-shadow duration-300"
              >
                <item.icon className="w-8 h-8 text-[#6B8E23] mb-5" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-[#1F2937] mb-4">{item.title}</h3>
                <ul className="space-y-2.5">
                  {item.points.map((point, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#5A5A5A]">
                      <Check className="w-4 h-4 text-[#6B8E23] flex-shrink-0 mt-0.5" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Decorative divider */}
      <div className="flex justify-center py-6 bg-white">
        <img src={`${assetsUrl}/botanical-divider.svg`} alt="" className="w-full max-w-md h-6 object-contain opacity-70" aria-hidden />
      </div>

      {/* Section 3 — Materials We Use */}
      <section className="py-16 lg:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#1F2937] text-center mb-14 lg:mb-16">
            Materials We Use
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {MATERIALS.map((item, i) => (
              <article
                key={i}
                className="bg-white rounded-xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-[#E8E8E8] hover:scale-[1.02] hover:shadow-[0_8px_28px_rgba(0,0,0,0.08)] transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#F5F2ED]">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 lg:p-7">
                  <h3 className="text-lg font-semibold text-[#1F2937] mb-4">{item.title}</h3>
                  <ul className="space-y-2.5">
                    {item.points.map((point, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                        <Check className="w-4 h-4 text-[#6B8E23] flex-shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4 — Our Environmental Impact */}
      <section className="py-16 lg:py-24 bg-[#4F6F52]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#1F2937] text-center mb-14 lg:mb-16">
            Our Environmental Impact
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {IMPACT_STATS.map((item, i) => (
              <div
                key={i}
                className="bg-white/90 rounded-xl p-7 lg:p-8 text-center border border-[#4F6F52]/15 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
              >
                <item.icon className="w-10 h-10 text-[#4F6F52] mx-auto mb-4" />
                <div className="text-xl lg:text-2xl font-semibold text-[#1F2937] mb-2">
                  {item.value}
                </div>
                <div className="text-sm text-[#5A5A5A]">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 — Social Responsibility */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="rounded-xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] aspect-[4/3] lg:aspect-auto lg:min-h-[340px] bg-[#F5F2ED]">
              <img
                src={`${baseUrl}/manufacturing2.jpeg`}
                alt="Responsible manufacturing"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#1F2937] mb-7">
                Social Responsibility
              </h2>
              <p className="text-[#5A5A5A] text-base leading-relaxed mb-5">
                Our manufacturing partners share our commitment to safe workplaces, fair wages, and
                dignified work. We work with facilities that meet international labor and safety
                standards.
              </p>
              <p className="text-[#5A5A5A] text-base leading-relaxed mb-6">
                From raw material to finished bag, we prioritize transparency and long-term
                relationships with artisans and factories.
              </p>
              <ul className="space-y-3">
                {[
                  'Safe and ethical working conditions',
                  'Fair wages and timely payments',
                  'No child or forced labor',
                  'Community and skill development',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-[#1F2937]">
                    <Check className="w-4 h-4 text-[#6B8E23] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6 — Certifications */}
      <section className="py-16 lg:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#1F2937] text-center mb-14 lg:mb-16">
            Certifications & Standards
          </h2>
          <div className="flex flex-wrap justify-center gap-6 lg:gap-8">
            {CERTIFICATIONS.map((item, i) => (
              <div
                key={i}
                className="flex flex-col items-center gap-4 p-6 rounded-xl bg-white border border-[#E8E8E8] shadow-[0_1px_3px_rgba(0,0,0,0.04)] min-w-[140px]"
              >
                <item.icon className="w-10 h-10 text-[#4F6F52]" />
                <span className="text-sm font-medium text-[#1F2937] text-center">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7 — Final CTA */}
      <section className="py-20 lg:py-28 bg-[#4F6F52]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-6">
            Build Your Sustainable Brand With Us
          </h2>
          <p className="text-white/90 text-base sm:text-lg mb-10">
            Partner with Bags4Ever for export-quality, eco-friendly bags. Request a wholesale quote
            and join brands that choose sustainability.
          </p>
          <Button
            onClick={onRequestQuote}
            className="bg-[#6B8E23] hover:bg-[#5a7d1e] text-white rounded-md px-8 py-5 text-sm font-medium transition-all duration-300 shadow-[0_2px_12px_rgba(107,142,35,0.25)] hover:shadow-[0_4px_20px_rgba(107,142,35,0.3)]"
          >
            Request Wholesale Quote
          </Button>
        </div>
      </section>
    </div>
  );
}
